import { type TemplateResult } from 'lit';
import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { EntityContent } from 'src/content/entity-content.js';
import '../../library/editors/index.js';
import '../../library/editors/components/d-edit-classification.js';
import '../../library/editors/elements/d-select-dropdown.js';
import '../../library/fields/index.js';
import '../../library/components/d-signatures-section';
import 'src/library/components/d-form';
import 'src/library/elements/d-wrap';
import 'src/library/fields/d-view-text-with-icons';
import 'src/content/event-occurrences/d-edit-assignees.js';
import { capitalize, isEmptyOrInvalidString, joinWithAnd, numberWord, relativeDaysAsText } from 'src/utilities/text';
import { MeetingMessageDialog } from 'src/content/meeting-occurrences/meeting-message-dialog';
import type { UpdateSectionItem } from '../update-section-item';
import type { ListDefinition } from '../list-definition';
import type { MessageForSend } from '../meeting-occurrences/message-for-send';
import type { MeetingMessageDialogInput } from '../meeting-occurrences/meeting-message-dialog-input';
import type { MeetingMessageDialogResult } from '../meeting-occurrences/meeting-message-dialog-result';
import type { ReportView } from './report-view';
import type { ReportViewEditItem } from './report-view-edit-item';
import type { SectionField } from 'src/library/components/section-field';
import type { Revision } from 'src/content/revision';
import type { FormElement, FormMode } from 'src/library/components/form';
import { signatureStatusStyles } from 'src/library/signature-status-styles';
import type { IconForText, TextWithIcon } from 'src/library/fields/text-with-icons';
import type { SelectDropdownOption } from 'src/library/editors/elements/select-dropdown-option';
import type { FormDialogResult } from 'src/library/components/form-dialog-result';
import { FormDialog } from 'src/library/components/form-dialog';
import {
  extractFormData,
  formResponseCount,
  getFormContentWithValuesForView,
} from 'src/store/selectors/form-functions';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import type { PdfField } from 'src/pdf/types';
import { filterFormStructure } from 'src/pages/status-page/filter-form-structure';
import type { UpdateFormValuesDetail } from 'src/pages/status-page/update-form-values-detail';
import { assertIsDefined } from 'src/assertIsDefined';
import { getFieldsWithValuesForPdf } from 'src/store/selectors/pdf-template-functions';
import { LocalDate } from 'src/utilities/local-date';
import { t } from 'src/i18n';

/**
 *
 * FIX Meeting header with employee and time?
 */
@customElement('d-report-view')
export class DReportView extends EntityContent<ReportView, ReportViewEditItem> {
  static readonly styles = EntityContent.styles.concat(
    signatureStatusStyles,
    css`
      .meta-section {
        border: 1px solid var(--borderColor);
        border-bottom: none;
        background: hsl(0, 0%, 96%);
        padding: 4px 16px;
        line-height: 150%;
      }

      .meta-section > d-section:first-child {
        border: none;
      }

      .meta-section-actions {
        margin: -4px -2px -12px 0;
      }

      .meta-section .header {
        margin-top: -6px;
        padding: 20px 0 10px 44px;
        background: url(/images/forms-color.svg) -4px 8px no-repeat;
        background-size: 44px auto;
        font-size: 20px;
        font-weight: bold;
        color: var(--text-color);
      }
    `,
  );
  @property({ type: Boolean })
  isEmployee = false;
  @property({ type: Object })
  organization: any = {};
  @property({ type: Array })
  contacts = [];
  @property({ type: String })
  currentEmployeeUuid = '';
  @property({ type: String })
  employeeInterviewUuid = '';
  @property({ type: Object })
  relatedMeetingEvent = {};
  @property({ type: String })
  meetingEventHref = '';
  @property({ type: String })
  meetingPresent = '';
  @property({ type: String })
  meetingAgenda = '';
  @property({ type: Boolean, attribute: 'no-animation' })
  noAnimation = false;

  protected get preventDelete(): boolean {
    return this.entityView.isForm && !this.entityView.currentUserIsResponsible;
  }

  protected localPdf() {
    if (this.entityView.isForm) {
      return true;
    }
    return false;
  }

  protected get currentItemSecondaryLabel() {
    return this.entityView.secondaryLabel || '';
  }

  protected get currentItemSublabel() {
    if (this.entityView.isMeetingReport) {
      return this.entityView.meetingOccurrenceDisplay ?? '';
    }
    if (this.entityView.isForm && this.entityView.formSubtitle) {
      return this.entityView.formSubtitle;
    }
    return '';
  }

  private get formInfoHeader() {
    if (this.entityView.anonymousForm) {
      return t('reports.anonymousQuestionnaire');
    }
    return t('reports.form');
  }

  private get formMode(): FormMode {
    if (this.entityView.currentUserIsResponsible) {
      if (this.mode === 'edit') {
        return 'editSettings';
      }
      return 'viewAsResponsible';
    }
    if (this.entityView.currentUserIsRespondent) {
      if (this.mode === 'edit') {
        throw new Error('Illegal state');
      }
      return 'viewAsRespondent';
    }
    return 'none';
  }

  private get formIsClosed() {
    return (
      this.entityView.formResponseDeadline !== '' &&
      LocalDate.fromString(this.entityView.formResponseDeadline).isBefore(this.entityView.today)
    );
  }

  private get formResponseDeadlineText() {
    if (!this.entityView.formResponseDeadline) {
      return t('reports.noDeadline');
    }
    const deadlineDate = LocalDate.fromString(this.entityView.formResponseDeadline);
    const days = this.entityView.today.until(deadlineDate);
    let daysText = '';
    if (days < 0) {
      daysText = t('reports.expiredSuffix');
    }
    if (days > -1) {
      daysText = ' (' + relativeDaysAsText(this.entityView.formResponseDeadline, this.entityView.today) + ')';
    }
    return deadlineDate.toStringForDisplay() + daysText;
  }

  private get formData() {
    if (this.entityView.isForm) {
      return extractFormData(this.entityView.elements);
    }
  }

  private get isSurvey() {
    if (this.formData) {
      return this.formData.respondentMultipleFieldsCount > 0;
    }
    return false;
  }

  private get hasResponsibleFields(): boolean {
    if (this.formData) {
      return this.formData.responsibleFieldsCount > 0;
    }
    return false;
  }

  private get currentUserHasRole() {
    return this.entityView.currentUserIsResponsible || this.entityView.currentUserIsRespondent;
  }

  private get formTypeInfoContent(): string {
    if (this.isSurvey) {
      if (this.hasResponsibleFields) {
        return t('reports.formTypeInfoTwoPhase');
      }
      return t('reports.formTypeInfoSurvey', { anonymous: this.entityView.anonymousForm ? ' anonymt ' : '' });
    }
    return t('reports.formTypeInfoSimple');
  }

  private get formPreviewText() {
    return t('reports.formPreviewText');
  }

  private get respondents(): SelectDropdownOption[] {
    const result: SelectDropdownOption[] = [];
    if (this.entityView.availableEmployeesForRespondent) {
      this.entityView.availableEmployeesForRespondent.forEach((r) => {
        if (this.entityView.formRespondentEmployees?.includes(r.value)) {
          result.push(r);
        }
      });
    }
    if (this.entityView.availableContactsForRespondent) {
      this.entityView.availableContactsForRespondent.forEach((r) => {
        if (this.entityView.formRespondentContacts?.includes(r.value)) {
          result.push(r);
        }
      });
    }
    return result;
  }

  private get respondentsWithIcons(): TextWithIcon[] {
    return this.respondents.map((r) => {
      let icon: IconForText = 'spinner';
      if (this.formIsClosed) {
        icon = 'x';
      }
      if (this.respondentHasAnswered(r.value)) {
        icon = 'check';
      }
      return {
        icon,
        text: r.text,
      };
    });
  }

  private get responseText(): string {
    if (this.formData && this.isSurvey && this.respondents.length > 1) {
      let responded: number;
      let notResponded = 0;
      if (this.entityView.anonymousForm) {
        responded = formResponseCount(this.entityView.elements);
      } else {
        responded = this.respondentsWithIcons.filter((i) => {
          return i.icon === 'check';
        }).length;
        notResponded = this.respondentsWithIcons.filter((i) => {
          return i.icon !== 'check';
        }).length;
      }
      if (responded === 0) {
        return t('reports.noOneHasResponded');
      } else {
        let waiting = '';
        if (!this.formIsClosed && !this.entityView.anonymousForm && notResponded > 0) {
          waiting = t('reports.waitingForSuffix', { count: numberWord(notResponded) });
        }
        return t('reports.hasResponded', { count: capitalize(numberWord(responded)) }) + waiting;
      }
    }
    return '';
  }

  private get disabledUuids(): string[] {
    if (this.editItem) {
      const formResponsibleFunction = this.entityView.functionsWithEmployees.find((f) => {
        return f.functionUuid === this.editItem?.formResponsibleFunction;
      });
      if (formResponsibleFunction) {
        if (
          this.entityView.currentUserUuid &&
          !formResponsibleFunction.employees.includes(this.entityView.currentUserUuid)
        ) {
          return [this.entityView.currentUserUuid];
        }
      }
    }
    return [];
  }

  async onEditAsResponsible(e) {
    e.preventDefault();
    await this.showFormDialog('editAsResponsible');
  }

  renderSurveyFormResponsiblePrompt() {
    let text: string;
    let actionText: string = '';
    if (!this.formIsClosed) {
      text = t('reports.responsibleSummarizeWhenDeadlinePassed');
    } else {
      if (!this.formData?.responsibleResponseCount) {
        text = t('reports.deadlinePassedSummarizeNow');
        actionText = t('reports.summarize');
      } else {
        text = t('reports.editYourAnswersAsResponsible');
        actionText = t('reports.editAnswer');
      }
    }
    return html`
      <d-section>
        <d-wrap split baseline>
          <d-view-info info-list .content=${'<ul><li>' + text + '</li></ul>'}></d-view-info>
          <div>
            ${this.formIsClosed
              ? html` <d-action style="margin-top: -12px" @click=${(e) => this.onEditAsResponsible(e)}
                  >${actionText}
                </d-action>`
              : nothing}
          </div>
        </d-wrap>
      </d-section>
    `;
  }

  renderSurveyFormResponsibleRespondentPrompt() {
    if (this.entityView.currentUserIsRespondent && !this.formIsClosed) {
      let text: string;
      let actionText: string;
      if (!this.respondentHasAnswered(this.entityView.currentUserUuid ?? '')) {
        text = t('reports.youAreRespondentNotAnsweredYet');
        actionText = t('reports.answer');
      } else {
        text = t('reports.stillEditYourAnswersAsRespondent');
        actionText = t('reports.editAnswer');
      }
      return html`
        <d-section>
          <d-wrap split baseline>
            <d-view-info info-list .content=${'<ul><li>' + text + '</li></ul>'}></d-view-info>
            <d-action style="margin-top: -12px" @click=${(e) => this.onOpenFormDialog(e)}>${actionText}</d-action>
          </d-wrap>
        </d-section>
      `;
    }
    return html``;
  }

  renderFormResponsibleHeader() {
    return html`
      <div class="meta-section">
        <d-section>
          <d-wrap split-reverse class="meta-section-header fullWidth">
            <div class="meta-section-actions">
              ${!this.isSurvey
                ? html` <d-action @click=${(e) => this.onEditAsResponsible(e)}>${t('reports.answer')}</d-action> `
                : nothing}
              <d-action @click=${() => this.onEditModeOn()}>${t('reports.settings')}</d-action>
            </div>
            <div class="header">${this.formInfoHeader}</div>
          </d-wrap>
          <d-view-info .content=${this.formTypeInfoContent} class="fullWidth"></d-view-info>
        </d-section>
        ${this.renderFieldSection('reports', this.viewFields(), this.entityView)}
        ${this.isSurvey
          ? html`
              ${this.renderSurveyFormResponsibleRespondentPrompt()}
              ${this.hasResponsibleFields ? this.renderSurveyFormResponsiblePrompt() : nothing}
            `
          : nothing}
      </div>
    `;
  }

  renderFormRespondentHeader() {
    let text: string;
    let actionText: string = '';
    if (!this.formIsClosed) {
      if (!this.respondentHasAnswered(this.entityView.currentUserUuid ?? '')) {
        text = this.entityView.formPrompt ?? '';
        actionText = t('reports.answer');
      } else {
        text = t('reports.stillEditYourAnswers');
        actionText = t('reports.editAnswer');
      }
    } else {
      text = t('reports.formClosedForCompletion');
    }
    return html`
      <div class="meta-section">
        <d-section>
          <d-wrap split baseline>
            <d-view-info .content=${'<ul><li>' + text + '</li></ul>'} class="fullWidth" info-list></d-view-info>
            <div style="margin-top: -6px">
              ${actionText !== ''
                ? html` <d-action @click=${(e) => this.onOpenFormDialog(e)}>${actionText}</d-action> `
                : nothing}
            </div>
          </d-wrap>
        </d-section>
      </div>
    `;
  }

  /**
   * Header stating that the user has no role in the form and providing information about the form responsible,
   * so that the user knows who to contact for information about the form.
   */
  renderNoRoleHeader() {
    return html`
      <div class="meta-section">
        <d-section>
          <d-view-info
            .content=${'<ul><li>' + t('reports.youHaveNoRoleInForm') + '</li></ul>'}
            class="fullWidth"
            info-list
          ></d-view-info>
        </d-section>
        ${this.renderFieldSection('reports', this.viewFields(), this.entityView)}
      </div>
    `;
  }

  renderFormHeader(): TemplateResult<1> {
    if (this.entityView.currentUserIsResponsible) {
      return this.renderFormResponsibleHeader();
    }
    if (this.entityView.currentUserIsRespondent) {
      return this.renderFormRespondentHeader();
    }
    return this.renderNoRoleHeader();
  }

  private get usersForPdf(): SelectDropdownOption[] {
    if (this.entityView.peopleAndFunctions) {
      return this.entityView.peopleAndFunctions
        .filter((item) => {
          return item.type !== 'function';
        })
        .map((item) => {
          return {
            value: item.uuid,
            text: item.fullName,
          };
        });
    }
    return [];
  }

  private get formContentWithValuesForPdf() {
    return getFormContentWithValuesForView(
      this.entityView.elements,
      this.formMode,
      this.usersForPdf,
      this.entityView.currentUserUuid ?? '',
      this.respondents,
      this.entityView.anonymousForm ?? false,
    );
  }

  protected pdfFields(): PdfField[] {
    if (this.entityView.isForm) {
      const result: PdfField[] = [];
      getFieldsWithValuesForPdf(this.viewFields(), this.lists(), this.entityView)
        .concat(this.formContentWithValuesForPdf)
        .forEach((item) => {
          if (item.type === 'header') {
            result.push({
              type: 'header',
              text: item.label,
            });
          }
          if (
            item.type === 'view-html' ||
            item.type === 'view-text' ||
            item.type === 'view-date' ||
            item.type === 'view-assignees'
          ) {
            if ('userValues' in item && item.userValues) {
              result.push({
                type: 'multiple-values',
                label: item.label,
                values: item.userValues,
              });
            } else {
              result.push({
                type: 'view-text',
                label: item.label,
                text: item.value ?? '',
              });
            }
          }
          if (item.type === 'view-html-with-graph' && 'barGraphItems' in item && item.barGraphItems) {
            result.push({
              type: 'status-bar',
              label: item.label,
              barGraphItems: item.barGraphItems,
              values: item.userValues ?? [],
            });
          }
        });
      return result;
    }
    return super.pdfFields();
  }

  renderForm(elements: FormElement[], mode: FormMode): TemplateResult<1> {
    return html` <d-form
      .mode=${mode}
      .content=${elements}
      .isSurvey=${this.entityView.isSurvey ?? false}
      .anonymous=${this.entityView.anonymousForm ?? false}
      .userNames=${this.entityView.userNames ?? []}
      .respondents=${this.respondents}
      .currentUserUuid=${this.entityView.currentUserUuid ?? ''}
      .contentStickyTop=${this.contentStickyTop}
      @item-changed=${this.onFormChanged}
    ></d-form>`;
  }

  renderViewMode(): TemplateResult<1> {
    if (this.entityView.isForm) {
      return html`
        ${this.renderFormHeader()}
        ${this.currentUserHasRole ? html` ${this.renderForm(this.entityView.elements, this.formMode)}` : nothing}
      `;
    }
    return html` ${this.renderFieldSection('reports', this.viewFields(), this.entityView)} `;
  }

  async initializeEditItem() {
    let employee = '';
    let partnerContactUuid = '';
    const employeeAuthor = this.entityView.availableEmployeesForAuthor.find(
      (item) => item.value === this.entityView.author,
    );
    if (employeeAuthor) {
      employee = employeeAuthor.value;
    } else {
      const contactAuthor = this.entityView.availableContactsForAuthor.find(
        (item) => item.value === this.entityView.author,
      );
      if (contactAuthor) {
        partnerContactUuid = contactAuthor.value;
      }
    }
    let formResponseDeadline = this.entityView.formResponseDeadline;
    if (this.isSurvey && !this.entityView.formResponseDeadline) {
      formResponseDeadline = this.entityView.today.plusDays(14).toString();
    }
    this.editItem = {
      employee,
      partnerContactUuid,
      pages: this.entityView.pages,
      reportDate: this.entityView.reportDate ?? this.entityView.today.toString(),
      accessControl: this.entityView.accessControl,
      classification: this.entityView.classification,
      content: this.entityView.content,
      name: this.entityView.name,
      createdFromTemplate: this.entityView.createdFromTemplate,
      isForm: this.entityView.isForm,
      anonymousForm: this.entityView.anonymousForm,
      formSubtitle: this.entityView.formSubtitle,
      formResponseDeadline,
      formResponsibleFunction: this.entityView.formResponsibleFunction,
      formResponsibleEmployees: this.entityView.formResponsibleEmployees,
      formResponsibleContacts: this.entityView.formResponsibleContacts,
      formRespondentEmployees: this.entityView.formRespondentEmployees,
      formRespondentContacts: this.entityView.formRespondentContacts,
      elements: this.entityView.elements,
    };
  }

  doneDisabled(): boolean {
    return isEmptyOrInvalidString(this.editItem?.name);
  }

  onFormChanged(e: CustomEvent<{ value: FormElement[] }>) {
    e.stopPropagation();
    if (this.editItem) {
      this.editItem = { ...this.editItem, elements: e.detail.value };
    }
  }

  renderEditItem(item: ReportViewEditItem): TemplateResult<1> {
    if (this.entityView.isForm) {
      return html`
        <div class="meta-section">
          <d-section>
            <d-wrap split-reverse baseline class="meta-section-header fullWidth">
              <div class="meta-section-actions"></div>
              <div class="header">${this.formInfoHeader}</div>
            </d-wrap>
            <d-view-info .content=${this.formTypeInfoContent} class="fullWidth"></d-view-info>
          </d-section>
          ${this.renderFieldSection('reports', this.editFields(item), item, true)}
          ${this.renderEditClassification(item, true)} ${super.renderEditSelectPages(item, true)}
        </div>
        ${!this.entityView.isConfirmedEntity
          ? html` <d-section>
                <d-label big label=${t('reports.contentPreview')}></d-label>
                <d-view-info .content=${this.formPreviewText} class="fullWidth"></d-view-info>
              </d-section>
              ${this.renderForm(this.entityView.elements, 'viewAsResponsible')}`
          : nothing}
      `;
    }
    return html` ${this.renderEditClassification(item)}
    ${this.renderFieldSection('reports', this.editFields(item), item)}`;
  }

  renderRespondents() {
    if (this.respondents.length) {
      return html`
        <div class="fullWidth">
          <d-wrap split tight baseline>
            ${this.entityView.anonymousForm
              ? html`
                  <d-view-text
                    field="reports_formRespondents"
                    .value=${joinWithAnd(this.respondents.map((r) => r.text))}
                  ></d-view-text>
                `
              : html`
                  <d-view-text-with-icons
                    field="reports_formRespondents"
                    .items=${this.respondentsWithIcons}
                    wrap-label
                    .noAnimation=${this.noAnimation}
                  ></d-view-text-with-icons>
                `}
            ${!this.formIsClosed
              ? html`
                  <d-action style="margin: -5px -3px -6px 0" @click=${() => this.onInviteFormRespondents()}
                    >${t('reports.invite')}
                  </d-action>
                `
              : html`<div></div>`}
          </d-wrap>
          <div style="font-size: 13px; font-weight: 200">${this.responseText}</div>
        </div>
      `;
    }
    return html`
      <d-view-text
        field="reports_formRespondents"
        .value=${t('reports.noRespondents')}
        class="fullWidth"
        wrap-label
      ></d-view-text>
    `;
  }

  renderEditResponsible(item: ReportViewEditItem) {
    if (this.editItem) {
      return html`
        <d-edit-assignees
          theme-page
          function-required
          class="fullWidth"
          field="reports_formResponsible"
          .assignedToFunction=${this.editItem.formResponsibleFunction ?? ''}
          .assignedToEmployees=${this.editItem.formResponsibleEmployees ?? []}
          .assignedToContacts=${this.editItem.formResponsibleContacts ?? []}
          .functions=${this.entityView.availableFunctionsForForm ?? []}
          .employees=${this.entityView.availableEmployeesForResponsible ?? []}
          .contacts=${this.entityView.availableContactsForResponsible ?? []}
          .disabledUuids=${this.disabledUuids}
          @assignment-changed=${(
            e: CustomEvent<{
              assignedToEmployees: string[];
              assignedToFunction: string;
              assignedToContacts: string[];
            }>,
          ) => this.formResponsibleChanged(e, item)}
        >
        </d-edit-assignees>
      `;
    }
    return html``;
  }

  renderEditRespondents(item: ReportViewEditItem) {
    if (this.editItem) {
      return html`
        <d-edit-assignees
          theme-page
          class="fullWidth"
          field="reports_formRespondents"
          .assignedToEmployees=${this.editItem.formRespondentEmployees ?? []}
          .assignedToContacts=${this.editItem.formRespondentContacts ?? []}
          .employees=${this.entityView.availableEmployeesForRespondent ?? []}
          .contacts=${this.entityView.availableContactsForRespondent ?? []}
          @assignment-changed=${(
            e: CustomEvent<{
              assignedToEmployees: string[];
              assignedToFunction: string;
              assignedToContacts: string[];
            }>,
          ) => {
            e.stopPropagation();
            this.editItem = {
              ...item,
              formRespondentEmployees: e.detail.assignedToEmployees,
              formRespondentContacts: e.detail.assignedToContacts,
            };
          }}
        >
        </d-edit-assignees>
      `;
    }
    return html``;
  }

  renderRevisionItems(revisions: Revision[]) {
    if (this.entityView.isForm) {
      return html``;
    }
    return super.renderRevisionItems(revisions);
  }

  protected hideSaveAsNew() {
    if (this.entityView.isForm) {
      return true;
    }
    return super.hideSaveAsNew();
  }

  protected hideEdit() {
    if (this.entityView.isForm) {
      return true;
    }
    return super.hideEdit();
  }

  protected viewFields(): SectionField[] {
    return [
      {
        field: 'classificationText',
        fieldForLabel: 'generalFields_access',
        type: 'view-text',
        class: 'fullWidth',
        condition: !this.entityView.isForm && this.entityView.classificationText !== '',
      },
      {
        field: 'reportDate',
        type: 'view-date',
        format: 'date-short',
        class: 'minWidth300',
        condition: !this.entityView.isForm,
      },
      {
        field: 'reportDate',
        fieldForLabel: 'formStartDate',
        type: 'view-date',
        format: 'date-short',
        class: 'minWidth300',
        condition: this.entityView.isForm && this.currentUserHasRole,
      },
      {
        field: 'formResponseDeadline',
        type: 'view-date',
        relativeDate: true,
        today: this.entityView.today,
        class: 'minWidth300',
        condition: this.entityView.isForm && this.currentUserHasRole,
      },
      {
        field: 'formResponsible',
        type: 'view-assignees',
        peopleAndFunctions: this.entityView.peopleAndFunctions,
        pronoun: 'subject',
        class: 'fullWidth',
        condition: this.entityView.isForm,
      },
      {
        field: 'formRespondents',
        type: 'custom',
        render: () => this.renderRespondents(),
        condition:
          this.entityView.isForm &&
          this.entityView.currentUserIsResponsible &&
          this.formData &&
          this.formData.respondentFieldsCount > 0,
      },
      {
        field: 'formRespondents',
        type: 'view-assignees',
        peopleAndFunctions: this.entityView.peopleAndFunctions,
        class: 'minWidth300',
        condition: this.entityView.isForm,
        excludeFromScreen: true,
      },
      {
        field: 'author',
        type: 'view-text',
        options: this.entityView.availableEmployeesForAuthor.concat(this.entityView.availableContactsForAuthor),
        class: 'minWidth300',
        condition: !this.entityView.isForm,
      },
      {
        field: '',
        type: 'custom',
        render: () => this.renderSigningSection(),
        condition: this.entityView.hasSigningModule && !this.entityView.deleted,
      },
      {
        field: 'content',
        type: 'view-html',
        condition: !this.entityView.isForm,
      },
    ];
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: 'attachments',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderAttachments(),
      },
    ];
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return this.entityView;
  }

  protected renderAttachments() {
    if (this.entityView.isForm && !this.entityView.currentUserIsResponsible) {
      return html``;
    }
    return super.renderAttachments();
  }

  protected renderEditSelectPages(item: any): TemplateResult<1> {
    if (this.entityView.isForm) {
      return html``;
    }
    return super.renderEditSelectPages(item);
  }

  protected renderUpdateSection() {
    if (this.entityView.isForm) {
      return html``;
    }
    return super.renderUpdateSection();
  }

  protected renderEditClassification(item: any, themePage: boolean = false): TemplateResult<1> {
    if (this.entityView.isForm) {
      return html``;
    }
    return super.renderEditClassification(item, themePage);
  }

  private respondentHasAnswered(uuid: string) {
    const formData = extractFormData(this.entityView.elements, uuid);
    return formData.currentUserResponseCount > 0;
  }

  private async onOpenFormDialog(e) {
    e.preventDefault();
    await this.showFormDialog('editAsRespondent');
  }

  private async showFormDialog(mode: 'editAsRespondent' | 'editAsResponsible') {
    assertIsDefined(this.entityView.currentUserUuid);
    const result: FormDialogResult = await FormDialog.open({
      title: this.entityView.name,
      content: this.entityView.elements,
      prompt: this.entityView.formPrompt ?? '',
      isSurvey: this.entityView.isSurvey ?? false,
      anonymous: this.entityView.anonymousForm,
      userNames: this.entityView.userNames,
      currentUserUuid: this.entityView.currentUserUuid,
      mode,
    });
    if (result.action === 'cancel') {
      return;
    }
    if (result.action === 'done') {
      const values = filterFormStructure(result.content, this.entityView.currentUserUuid);

      this.dispatchEvent(
        new CustomEvent<UpdateFormValuesDetail>('update-form-values', {
          bubbles: true,
          composed: true,
          detail: {
            type: 'reports',
            uuid: this.entityView.uuid,
            content: values,
          },
        }),
      );
    }
  }

  private onInviteFormRespondents() {
    let deadlineText = '';
    if (this.entityView.formResponseDeadline !== '') {
      deadlineText = t('reports.inviteDeadlineSuffix', { deadline: this.formResponseDeadlineText });
    }
    this.dispatchEvent(
      new CustomEvent('share-content', {
        bubbles: true,
        composed: true,
        detail: {
          message: t('reports.inviteMessage', { deadline: deadlineText }),
          contacts: this.entityView.contactsForShare,
          subject: t('reports.inviteSubject', {
            name: this.entityView.currentUserName,
            reportName: this.entityView.name,
          }),
          url: '/' + pageIds.status + '?dialog=form&report=' + this.entityView.uuid,
        },
      }),
    );
  }

  private formResponsibleChanged(
    e: CustomEvent<{
      assignedToEmployees: string[];
      assignedToFunction: string;
      assignedToContacts: string[];
    }>,
    item: ReportViewEditItem,
  ) {
    e.stopPropagation();
    const newFunction = e.detail.assignedToFunction;
    const newEmployees = e.detail.assignedToEmployees;
    const newContacts = e.detail.assignedToContacts;
    const currentEmployeeUuid = this.entityView.currentEmployeeUuid ?? '';
    const currentUserUuid = this.entityView.currentUserUuid ?? '';
    const formResponsibleFunction = this.entityView.functionsWithEmployees.find((f) => {
      return f.functionUuid === newFunction;
    });
    if (formResponsibleFunction) {
      if (!formResponsibleFunction.employees.includes(currentUserUuid)) {
        if (!newEmployees.includes(currentUserUuid) && !newContacts.includes(currentUserUuid)) {
          if (currentEmployeeUuid === currentUserUuid) {
            newEmployees.push(currentUserUuid);
          } else {
            newContacts.push(currentUserUuid);
          }
        }
      }
    }
    this.editItem = {
      ...item,
      formResponsibleFunction: newFunction,
      formResponsibleEmployees: newEmployees,
      formResponsibleContacts: newContacts,
    };
  }

  onAuthorChanged(e: CustomEvent<{ value: string }>, item: ReportViewEditItem) {
    e.stopPropagation();
    const employee = this.entityView.availableEmployeesForAuthor.find((item) => item.value === e.detail.value);
    if (employee) {
      this.editItem = {
        ...item,
        employee: e.detail.value,
        partnerContactUuid: '',
      };
    } else {
      const contact = this.entityView.availableContactsForAuthor.find((item) => item.value === e.detail.value);
      if (contact) {
        this.editItem = {
          ...item,
          employee: '',
          partnerContactUuid: e.detail.value,
        };
      }
    }
  }

  renderEditAuthor(item: ReportViewEditItem) {
    if (this.editItem) {
      return html`
        <d-select-dropdown
          placeholder=${t('reports.selectPerson')}
          field="reports_author"
          .options=${this.entityView.availableEmployeesForAuthor.concat(this.entityView.availableContactsForAuthor)}
          .value=${this.entityView.author}
          @value-changed=${(e: CustomEvent<{ value: string }>) => this.onAuthorChanged(e, item)}
        >
        </d-select-dropdown>
      `;
    }
    return html``;
  }

  private editFields(item: ReportViewEditItem): SectionField[] {
    return [
      {
        field: 'name',
        type: 'edit-text',
        class: 'fullWidth',
        placeholder: t('reports.fieldMustBeFilledOut'),
        markIfEmpty: true,
        autofocus: !this.entityView.isConfirmedEntity,
        selectOnFocus: !this.entityView.isConfirmedEntity,
        condition: !this.entityView.isMeetingReport && !this.entityView.isForm,
      },
      {
        field: 'formSubtitle',
        type: 'edit-text',
        class: 'fullWidth',
        selectOnFocus: !this.entityView.isConfirmedEntity,
        condition: this.entityView.isForm,
      },
      {
        field: 'reportDate',
        type: 'edit-date',
        class: 'minWidth200',
        condition: !this.entityView.isForm,
      },
      {
        field: 'reportDate',
        fieldForLabel: 'formStartDate',
        type: 'edit-date',
        class: 'minWidth200',
        condition: this.entityView.isForm,
      },
      {
        field: 'formResponseDeadline',
        type: 'edit-date',
        clear: !this.isSurvey,
        class: 'minWidth200',
        condition: this.entityView.isForm,
      },
      {
        type: 'custom',
        render: () => {
          return this.renderEditAuthor(item);
        },
        condition: !this.entityView.isForm,
      },
      {
        field: 'content',
        type: 'edit-html',
        docsForLinking: this.entityView.docsForLinking,
        condition: !this.entityView.isForm,
      },
      {
        type: 'custom',
        render: () => {
          return this.renderEditResponsible(item);
        },
        condition: this.entityView.isForm,
      },
      {
        type: 'custom',
        render: () => {
          return this.renderEditRespondents(item);
        },
        condition: this.entityView.isForm && this.formData && this.formData.respondentFieldsCount > 0,
      },
      {
        field: '',
        type: 'view-info',
        content: `<ul class="info-list"><li>` + t('reports.inviteRespondentsWhenReady') + `</li></ul>`,
        condition: this.entityView.isForm && !this.entityView.isConfirmedEntity,
      },
    ];
  }

  protected async onShareContent() {
    if (this.entityView.meetingOccurrence) {
      const meeting = this.entityView.meetingOccurrence;
      const input: MeetingMessageDialogInput = {
        type: 'report',
        name: this.entityView.meetingOccurrence.name,
        preventIncludeContent: this.entityView.classification && this.entityView.classification !== 'NONE',
        isEmployeeInterview: meeting.name?.toLowerCase().includes('medarbeidersamtale') ?? false,
        participants: this.entityView.meetingParticipants ?? [],
        people: this.entityView.availableMeetingMessageRecipients ?? [],
        classification: this.entityView.classification,
        accessControl: this.entityView.accessControl,
      };
      const result: MeetingMessageDialogResult = await MeetingMessageDialog.open(input);
      if (result.action === 'send') {
        const messageData = {
          occurrenceUuid: meeting.uuid,
          employeeRecipients: result.employeeRecipients,
          contactRecipients: result.contactRecipients,
          message: result.message,
          includeContent: result.includeContent,
        };
        this.dispatchEvent(
          new CustomEvent<MessageForSend>('send-meeting-report', {
            bubbles: true,
            composed: true,
            detail: messageData,
          }),
        );
      }
    } else {
      // For non-meeting reports, use the default share functionality
      super.onShareContent();
    }
  }

  private renderSigningSection() {
    return html` <d-signatures-section
      .entityView=${this.entityView}
      .signingDisabled=${false}
    ></d-signatures-section>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-report-view': DReportView;
  }
}
